import { zodResolver } from '@hookform/resolvers/zod';
import { TriggerHoldUntilReplyRuleType } from 'modules/workflows/constants/nodes/holdUntilReply';
import { HoldUntilReplyNodeData } from 'modules/workflows/types';
import { FC, PropsWithChildren } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { EditorBaseProps } from '../../types';
import {
  CAMPAIGN_ID,
  CUSTOM_DURATION,
  CUSTOM_DURATION_VALUE,
  DEFAULT_CAMPAIGN_ID,
  DEFAULT_HAS_OTHER_REPLY,
  DEFAULT_HOLD_DURATION,
  DEFAULT_REPLIES,
  DEFAULT_TRIGGER_RULE_TYPE,
  HAS_OTHER_REPLY,
  HOLD_DURATION,
  REPLIES,
  TRIGGER_RULE_TYPE,
} from './HoldUntilReply.constants';
import { parseRepliesForForm, parseTriggerRule } from './TriggerRuleUtils';
import { parseDuration } from './utils';

const schema = z.object({
  [TRIGGER_RULE_TYPE]: z.enum([
    TriggerHoldUntilReplyRuleType.SMS,
    TriggerHoldUntilReplyRuleType.WHATSAPP,
  ]),
  [REPLIES]: z.array(
    z.object({
      variants: z.array(z.object({ variant: z.string(), readOnly: z.boolean().optional() })),
    }),
  ),
  [CAMPAIGN_ID]: z.string().optional(),
  [HAS_OTHER_REPLY]: z.boolean(),
  [HOLD_DURATION]: z.number().min(-1).max(10080).optional(),
  [CUSTOM_DURATION]: z
    .object({
      expireDays: z.number().min(0).max(7),
      expireHours: z.number().min(0).max(168),
      expireMinutes: z.number().min(0).max(10080),
    })
    .superRefine((data, ctx) => {
      const { expireDays, expireHours, expireMinutes } = data;
      const exceedsMaxTime = expireDays * 24 * 60 + expireHours * 60 + expireMinutes > 10080;

      if (exceedsMaxTime) {
        ctx.addIssue({
          code: 'too_big',
          maximum: 10080,
          inclusive: true,
          message: 'Duration cannot exceed 7 days.',
          type: 'number',
        });
      }
    }),
});

export type HoldUntilReplyEditorProps = EditorBaseProps<HoldUntilReplyNodeData>;

export type HoldUntilReplyEditorFormSchemaType = Required<z.infer<typeof schema>>;

// likely need to implement something like this for when we have cloned data and it is attached to a campaign
// const getSchema = (clonedFromNodeData?: Partial<HoldUntilReplyNodeData>) =>
//   schema.superRefine((data, ctx) => {
//     if (clonedFromNodeData?.[WEBHOOK_FIELD] && !data?.nodeData?.[WEBHOOK_FIELD]) {
//       ctx.addIssue({
//         code: z.ZodIssueCode.custom,
//         message: VALIDATION_MESSAGES.CLONE,
//         path: [`nodeData.${WEBHOOK_FIELD}`],
//       });
//     }
//   });

/**
 * This form provider is created using [React Hook Form](https://react-hook-form.com/)
 * and [zod](https://zod.dev/) for schema validation
 */
export const HoldUntilReplyFormProvider: FC<PropsWithChildren<HoldUntilReplyEditorProps>> = ({
  nodeData,
  children,
}) => {
  const parsedTriggerRule = nodeData?.triggerRule ? parseTriggerRule(nodeData.triggerRule) : null;

  const type = parsedTriggerRule?.type || DEFAULT_TRIGGER_RULE_TYPE;
  const campaignId = parsedTriggerRule?.campaignId || DEFAULT_CAMPAIGN_ID;
  const replies = !!parsedTriggerRule?.replies?.length
    ? parseRepliesForForm(parsedTriggerRule.replies)
    : DEFAULT_REPLIES;
  const hasOtherReply = parsedTriggerRule?.hasOtherReply ?? DEFAULT_HAS_OTHER_REPLY;

  const currentDuration = {
    expireDays: nodeData?.expireDays || 0,
    expireHours: nodeData?.expireHours || 0,
    expireMinutes: nodeData?.expireMinutes || 0,
  };
  const holdDuration = parseDuration(currentDuration);
  const customDuration =
    holdDuration === CUSTOM_DURATION_VALUE ? currentDuration : DEFAULT_HOLD_DURATION;

  const methods = useForm<HoldUntilReplyEditorFormSchemaType>({
    defaultValues: {
      [TRIGGER_RULE_TYPE]: type,
      [CAMPAIGN_ID]: campaignId,
      [REPLIES]: replies,
      [HAS_OTHER_REPLY]: hasOtherReply,
      [HOLD_DURATION]: holdDuration,
      [CUSTOM_DURATION]: customDuration,
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
